import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Store, Check } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/i18n";
import { api, track, formatApiError, getSrc } from "@/lib/api";
import PhoneInput, { Honeypot, isValidPhone } from "@/components/PhoneInput";
import { toast } from "sonner";
import ConsentTick from "@/components/ConsentTick";

const inputCls = "w-full h-12 px-4 rounded-xl border border-[#E5E0D8] bg-white text-[#121316] outline-none focus:border-[#E05624] focus:ring-2 focus:ring-[#E05624]/20 transition";

const BIZ_TYPES = ["Saree store", "Jewellery store", "Boutique", "Wedding store", "Furniture store", "Electronics store", "Lifestyle store", "Gift store", "Local market", "Event business", "Other"];

export default function Partner() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", business_type: "", contact_name: "", phone: "", city: "Bhopal", message: "", website: "", consent: false });
  const [phoneErr, setPhoneErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { track("page_view", { page: "partner" }); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.contact_name.trim()) { toast.error(t("required")); return; }
    if (!isValidPhone(form.phone)) { setPhoneErr(t("phone_err")); return; }
    setPhoneErr("");
    if (!form.consent) { toast.error(t("consent_err")); return; }
    setSubmitting(true);
    try {
      await api.post("/partners", { ...form, src: getSrc() });
      track("partner_submit", { type: form.business_type });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      <div className="chela-container py-10 sm:py-16 max-w-3xl">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#5A5D65] hover:text-[#E05624]">
          <ArrowLeft size={16} /> {t("back_home")}
        </button>

        {done ? (
          <div className="mt-8 chela-card rounded-3xl p-8 text-center" style={{ transform: "none" }}>
            <div className="h-14 w-14 rounded-full bg-[#E8F7EE] flex items-center justify-center mx-auto"><Check size={28} className="text-[#1B8A4B]" /></div>
            <h2 className="mt-5 font-display font-bold text-2xl text-[#121316]">{t("pt_success")}</h2>
            <button onClick={() => navigate("/")} className="mt-6 inline-flex items-center h-12 px-7 rounded-full btn-dark font-bold">{t("back_home")}</button>
          </div>
        ) : (
          <form data-testid="partner-form" onSubmit={submit} className="mt-8 chela-card rounded-3xl p-6 sm:p-8 space-y-5" style={{ transform: "none" }}>
            <div>
              <div className="h-12 w-12 rounded-2xl bg-[#FFF1EC] flex items-center justify-center"><Store size={22} className="text-[#E05624]" /></div>
              <h1 className="mt-4 font-display font-extrabold text-3xl text-[#121316]">{t("pt_title")}</h1>
              <p className="mt-2 text-[#5A5D65]">{t("pt_sub")}</p>
            </div>

            <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">{t("f_business")}</span>
              <input data-testid="partner-business" value={form.business_name} onChange={set("business_name")} required className={inputCls} /></label>

            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">{t("f_biz_type")}</span>
                <select data-testid="partner-type" value={form.business_type} onChange={set("business_type")} className={inputCls}>
                  <option value="">{t("f_select")}</option>
                  {BIZ_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                </select></label>
              <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">{t("f_city")}</span>
                <input data-testid="partner-city" value={form.city} onChange={set("city")} className={inputCls} /></label>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">{t("f_contact")}</span>
                <input data-testid="partner-contact" value={form.contact_name} onChange={set("contact_name")} required className={inputCls} /></label>
              <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">{t("f_phone")}</span></label>
                <PhoneInput testid="partner-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} error={phoneErr} />
                <Honeypot value={form.website} onChange={(v) => setForm((f) => ({ ...f, website: v }))} />
            </div>

            <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">{t("f_message")}</span>
              <textarea data-testid="partner-message" value={form.message} onChange={set("message")} rows={3} className="w-full p-4 rounded-xl border border-[#E5E0D8] bg-white outline-none focus:border-[#E05624] focus:ring-2 focus:ring-[#E05624]/20 resize-none" /></label>

            <ConsentTick lang={lang} kind="enquiry" testid="partner-consent" checked={form.consent} onChange={(v) => setForm((f) => ({ ...f, consent: v }))} />
            <button data-testid="partner-submit" type="submit" disabled={submitting} className="w-full h-14 rounded-full btn-saffron font-bold text-base disabled:opacity-60">
              {submitting ? "..." : t("pt_submit")}
            </button>
            <p className="text-xs text-[#5A5D65] text-center">{t("b2b_benefit")}</p>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
